import {
    BAR_CHART,
    COLUMN_CHART,
    LINE_CHART,
    MULTI_CHART,
    PIE_CHART,
    STACKED_CHART,
} from '../../../utils/dashboardConsts';
import { calculatedMetricSumAggFunc, calculatedMetricValue } from '../../../utils/grids';
import { sortNumeric } from '../../../utils/sortUtil';
import { SHOW_ALL } from '../TopXChartDataFilter';
import { CURRENCY_METRIC_TYPE, DATE_DIMENSION_TYPE, PERCENTAGE_METRIC_TYPE } from './consts';
import { LEFT, RIGHT, isSideAxisChart, metricFormatType, resolveSides } from './axisConfig';

export const getMetricsFormat = reportColumns => {
    return reportColumns
        .filter(column => column.fieldType === 'metric')
        .map(metric => ({ name: metric.name, display_name: metric.displayName, displayFormat: metric.displayFormat }));
};

export const getMetricValueFormat = metric => {
    let formatString = '{value}';
    let valuePrefix = '';
    let valueSuffix = '';

    let valueDecimals = 0;
    const { displayFormat } = metric;
    if (displayFormat) {
        if (displayFormat.type === CURRENCY_METRIC_TYPE) {
            valuePrefix = (displayFormat.symbol || '').trim();
        } else if (displayFormat.type === PERCENTAGE_METRIC_TYPE) {
            valueSuffix = '%';
        }
        valueDecimals = displayFormat.precision || 0;
        formatString = `${valuePrefix}{value:.${valueDecimals}f}${valueSuffix}`;
    }
    return { valuePrefix, valueSuffix, valueDecimals, formatString };
};

const formatYAxisItem = (title, labelsFormat, yAxisIndex, overrides = {}) => {
    const item = {
        labels: {
            format: labelsFormat,
            style: {},
        },
        title: {
            text: title,
            style: {},
        },
        opposite: 'opposite' in overrides ? overrides.opposite : Boolean(yAxisIndex % 2),
        endOnTick: false,
        startOnTick: false,
    };
    const hasMin = overrides.min !== undefined && overrides.min !== null;
    const hasMax = overrides.max !== undefined && overrides.max !== null;
    // Emit explicit nulls (not omitted keys) so an in-place Highcharts update resets a
    // previously-set bound when the user clears it. A hard min takes precedence over the
    // "start at 0" soft min, so only apply softMin:0 when there is no custom min
    // (Highcharts otherwise lets softMin:0 pull the axis below the hard min).
    item.softMin = overrides.startFromZero !== false && !hasMin ? 0 : null;
    item.min = hasMin ? overrides.min : null;
    item.max = hasMax ? overrides.max : null;
    return item;
};

const getYAxis = metrics => {
    const yAxisList = [];
    const metricToYAxisMapping = new Map();

    metrics.forEach((metric, index) => {
        const { currentMetricFormat } = getMetricValueFormat(metric);
        yAxisList.push(formatYAxisItem(metric.display_name, currentMetricFormat, index));
        metricToYAxisMapping.set(metric.name, index);
    });

    return { yAxisList, metricToYAxisMapping };
};
const splitMetricsByTypes = metrics => {
    const metricTypesDict = new Map();
    metrics.forEach(metric => {
        const metricType = metric.displayFormat.type;
        if (!metricTypesDict.has(metricType)) {
            metricTypesDict.set(metricType, [metric]);
        } else {
            metricTypesDict.get(metricType).push(metric);
        }
    });
    return metricTypesDict;
};

const createYAxisListByMetricType = metricTypesDict => {
    const metricToYAxisMapping = new Map();
    const yAxisList = [];
    Array.from(metricTypesDict.keys()).forEach((key, index) => {
        const currMetrics = metricTypesDict.get(key);
        const { currentMetricFormat } = getMetricValueFormat(currMetrics[0]);
        currMetrics.forEach(metric => {
            metricToYAxisMapping.set(metric.name, index);
        });
        yAxisList.push({
            ...formatYAxisItem(currMetrics.map(metric => metric.display_name).join(' / '), currentMetricFormat, index),
        });
    });

    return { yAxisList, metricToYAxisMapping };
};

const getGroupedYAxis = metrics => {
    const metricTypesDict = splitMetricsByTypes(metrics);
    return createYAxisListByMetricType(metricTypesDict);
};

// Build the Y-axis list + metric→axis mapping from per-metric effective sides.
// One axis per (formatType × side) bucket, in first-seen metric order. With no
// overrides this reproduces getGroupedYAxis exactly (each format type maps to one
// side by its group-index parity). Per-side range supplies hard min/max.
const buildAxesFromSides = (metrics, effectiveSides, ranges) => {
    const order = [];
    const buckets = new Map();
    metrics.forEach(metric => {
        const side = effectiveSides[metric.name] === RIGHT ? RIGHT : LEFT;
        const key = `${metricFormatType(metric)}|${side}`;
        if (!buckets.has(key)) {
            buckets.set(key, { side, metrics: [] });
            order.push(key);
        }
        buckets.get(key).metrics.push(metric);
    });
    const metricToYAxisMapping = new Map();
    const yAxisList = order.map((key, index) => {
        const bucket = buckets.get(key);
        bucket.metrics.forEach(metric => metricToYAxisMapping.set(metric.name, index));
        const { currentMetricFormat } = getMetricValueFormat(bucket.metrics[0]);
        const title = bucket.metrics.map(metric => metric.display_name).join(' / ');
        const range = (ranges && ranges[bucket.side]) || {};
        return formatYAxisItem(title, currentMetricFormat, index, {
            opposite: bucket.side === RIGHT,
            min: range.min,
            max: range.max,
            startFromZero: range.startFromZero,
        });
    });
    return { yAxisList, metricToYAxisMapping };
};

const applyRangeToAxes = (axisData, range) => {
    if (!range) return axisData;
    const { min, max, startFromZero } = range;
    const hasMin = min !== undefined && min !== null;
    const hasMax = max !== undefined && max !== null;
    // Explicit nulls so clearing a bound (or turning off "start from 0") resets the
    // axis on an in-place Highcharts update. A custom min takes precedence over the
    // "start at 0" soft min, so drop softMin:0 whenever a hard min is set.
    return {
        ...axisData,
        yAxisList: axisData.yAxisList.map(axis => ({
            ...axis,
            softMin: startFromZero === false || hasMin ? null : 0,
            min: hasMin ? min : null,
            max: hasMax ? max : null,
        })),
    };
};

const getRowSeriesKey = (isPieChart, yAxis, row) => (isPieChart ? '' : yAxis.map(x => row[x.name]).join(' - '));

const getChartTypeForMultiChart = metricType => (metricType === PERCENTAGE_METRIC_TYPE ? LINE_CHART : COLUMN_CHART);

// multiple metrics && not multiple breakdowns - only one should exist

const isMultipleMetricsChart = (metrics, yAxis) => metrics.length > 1 && !yAxis.length;

export const isPieChart = chartType => chartType.name === PIE_CHART;

export const getChartDataSettings = (chartType, metrics, xAxis, yAxis, rowData, axisConfig = null) => {
    // refer this example for building the chart options: https://www.highcharts.com/demo/combo-multi-axes
    try {
        const categoryDict = new Map();
        let seriesList = [];

        const allowedCharts = [LINE_CHART, BAR_CHART, COLUMN_CHART, MULTI_CHART];
        const shouldGroupYAxis = allowedCharts.includes(chartType.name);
        const defaultAxisData = shouldGroupYAxis ? getGroupedYAxis(metrics) : getYAxis(metrics);
        let yAxisData;
        if (axisConfig && isSideAxisChart(chartType.name) && isMultipleMetricsChart(metrics, yAxis)) {
            const effectiveSides = resolveSides(axisConfig.sides, metrics);
            yAxisData = buildAxesFromSides(metrics, effectiveSides, {
                left: axisConfig.left,
                right: axisConfig.right,
            });
        } else if (axisConfig && axisConfig.range) {
            yAxisData = applyRangeToAxes(defaultAxisData, axisConfig.range);
        } else {
            yAxisData = defaultAxisData;
        }
        const { yAxisList, metricToYAxisMapping } = yAxisData;
        // Aggregate each row's value into the right category (X Axis) and series (Y Axis)
        metrics.forEach(metric => {
            const { valuePrefix, valueSuffix, valueDecimals } = getMetricValueFormat(metric);
            const metricType = metric.displayFormat.type;
            const seriesDict = new Map();

            rowData.forEach(row => {
                // Get or create category
                const rowCategoryName = xAxis.map(x => row[x.name]).join(' - ');
                if (!categoryDict.has(rowCategoryName)) {
                    categoryDict.set(rowCategoryName, { index: categoryDict.size });
                }

                const currentValue = row[metric.name];
                if (currentValue === 'N/A' || currentValue === 0) return;

                const rowCategory = categoryDict.get(rowCategoryName);

                // Get or create series
                const rowSeriesKey = getRowSeriesKey(isPieChart(chartType), yAxis, row);

                let currChartType;
                if (chartType.name === MULTI_CHART) {
                    currChartType = getChartTypeForMultiChart(metricType);
                } else if (chartType.name === STACKED_CHART) {
                    currChartType = COLUMN_CHART;
                } else {
                    currChartType = chartType.name;
                }

                if (!seriesDict.has(rowSeriesKey)) {
                    seriesDict.set(rowSeriesKey, {
                        name:
                            isMultipleMetricsChart(metrics, yAxis) || (!isPieChart(chartType) && rowSeriesKey === '')
                                ? metric.display_name
                                : rowSeriesKey,
                        data: [],
                        dataSorting: { enabled: false },
                        yAxis: metricToYAxisMapping.get(metric.name),
                        type: currChartType,
                        tooltip: {
                            valueSuffix,
                            valuePrefix,
                            valueDecimals,
                        },
                    });
                }
                // Get or create data entry in series
                const rowSeries = seriesDict.get(rowSeriesKey);
                if (rowCategory.index > rowSeries.data.length) {
                    // Fill the gaps with null
                    rowSeries.data = [
                        ...rowSeries.data,
                        ...new Array(rowCategory.index - rowSeries.data.length).fill(null),
                    ];
                }
                const dataArrayElement = rowSeries.data[rowCategory.index];
                if (dataArrayElement === undefined || dataArrayElement === null) {
                    rowSeries.data[rowCategory.index] = { name: rowCategoryName, y: currentValue };
                } else if (typeof currentValue === 'object') {
                    dataArrayElement.y = calculatedMetricSumAggFunc({ values: [dataArrayElement.y, currentValue] });
                } else {
                    dataArrayElement.y += currentValue;
                }
            });
            // Now that we've aggregated all the metrics, perform two last operations:
            // 1. If there are still calculated metrics that require the final calculation, perform it (e.g. CPC/CTR)
            // 2. Multiply by 100 for percentage type metrics
            seriesDict.forEach(series => {
                series.data.forEach(dataArrayElement => {
                    if (!dataArrayElement) {
                        return;
                    }

                    if (typeof dataArrayElement.y === 'object') {
                        dataArrayElement.y = calculatedMetricValue(dataArrayElement.y);
                    }

                    if (metric.displayFormat && metricType === PERCENTAGE_METRIC_TYPE) {
                        dataArrayElement.y *= 100;
                    }
                });
            });

            seriesList = [...seriesList, ...Array.from(seriesDict.values())];
        });

        // highcharts accepts arrays
        return [Array.from(categoryDict.keys()), seriesList, yAxisList];
    } catch (err) {
        console.log('error', err);
        return [[], []];
    }
};

// Cohort metrics use double underscores (__) in config but come back with single underscores (_) in data
// This function normalizes the metric name to match what's actually in the row data
const normalizeMetricName = (metricName, rowData) => {
    if (!rowData || !metricName) {
        return null;
    }

    if (rowData.hasOwnProperty(metricName)) {
        return metricName;
    }

    // Try converting double underscores to single underscores
    const singleUnderscoreVersion = metricName.replace(/__/g, '_');
    if (rowData.hasOwnProperty(singleUnderscoreVersion)) {
        return singleUnderscoreVersion;
    }

    return null;
};

export const getFilteredRowData = (rowsData = [], topXCount, metricForTopX) => {
    if (rowsData.length === 0 || topXCount === SHOW_ALL) {
        return rowsData;
    }

    const actualMetricName = normalizeMetricName(metricForTopX, rowsData[0]);
    if (!actualMetricName) {
        return rowsData;
    }

    return rowsData
        .sort((rowData1, rowData2) => {
            return sortNumeric(rowData1[actualMetricName], rowData2[actualMetricName], true);
        })
        .slice(0, topXCount);
};

export const hasDateDimension = (dimensions = []) =>
    dimensions.some(dim => dim?.displayFormat?.type === DATE_DIMENSION_TYPE);

export const getSortedRowData = (rowsData = [], metricName, xAxis = []) => {
    // Don't sort by metric value when x-axis is a date dimension - preserve chronological order
    if (rowsData.length === 0 || !metricName || hasDateDimension(xAxis)) {
        return rowsData;
    }

    const actualMetricName = normalizeMetricName(metricName, rowsData[0]);
    if (!actualMetricName) {
        return rowsData;
    }

    return [...rowsData].sort((rowData1, rowData2) => {
        let val1 = rowData1[actualMetricName];
        let val2 = rowData2[actualMetricName];

        if (typeof val1 === 'object' && val1 !== null) {
            val1 = calculatedMetricValue(val1);
        }
        if (typeof val2 === 'object' && val2 !== null) {
            val2 = calculatedMetricValue(val2);
        }

        return sortNumeric(val1, val2, true);
    });
};
